import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, MoreVertical, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  useDisableTwoFactorMutation,
  useGetTwoFactorStatusQuery,
  useTwoFactorSendCodeMutation,
  useTwoFactorSmsConfirmMutation,
  useTwoFactorSmsSetupMutation,
  useTwoFactorTotpConfirmMutation,
  useTwoFactorTotpSetupMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { TotpSetup } from "@/model/interface/TwoFactor";

export default function TwoFactorSection() {
  const { data: status, isLoading } = useGetTwoFactorStatusQuery();

  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsCode, setSmsCode] = useState("");

  const [totpDialogOpen, setTotpDialogOpen] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);
  const [totpCode, setTotpCode] = useState("");

  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCodeSent, setDisableCodeSent] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const [smsSetup, smsSetupResult] = useTwoFactorSmsSetupMutation();
  const [smsConfirm, smsConfirmResult] = useTwoFactorSmsConfirmMutation();
  const [startTotp, startTotpResult] = useTwoFactorTotpSetupMutation();
  const [confirmTotp, confirmTotpResult] = useTwoFactorTotpConfirmMutation();
  const [sendCode, sendCodeResult] = useTwoFactorSendCodeMutation();
  const [disable, disableResult] = useDisableTwoFactorMutation();

  const openSmsDialog = () => {
    setSmsPhone("");
    setSmsCode("");
    setSmsCodeSent(false);
    setSmsDialogOpen(true);
  };

  const onSendSmsSetupCode = async () => {
    try {
      await smsSetup({ phone: smsPhone }).unwrap();
      setSmsCodeSent(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to send the verification code"));
    }
  };

  const onConfirmSms = async (value: string) => {
    try {
      await smsConfirm({ code: value }).unwrap();
      setSmsDialogOpen(false);
      toast.success("Phone number verified. SMS verification is on.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Invalid code"));
    }
  };

  const openTotpDialog = async () => {
    setTotpCode("");
    setTotpSetup(null);
    setTotpDialogOpen(true);
    try {
      const setup = await startTotp().unwrap();
      setTotpSetup(setup);
    } catch (error) {
      setTotpDialogOpen(false);
      toast.error(apiErrorMessage(error, "Failed to start authenticator setup"));
    }
  };

  const onConfirmTotp = async (value: string) => {
    try {
      await confirmTotp({ code: value }).unwrap();
      setTotpDialogOpen(false);
      toast.success("Authenticator app connected.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Invalid code"));
    }
  };

  const openDisableDialog = () => {
    setDisableCode("");
    setDisableCodeSent(false);
    setDisableDialogOpen(true);
  };

  const onSendDisableCode = async () => {
    try {
      await sendCode().unwrap();
      setDisableCodeSent(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to send the code"));
    }
  };

  const onDisable = async (value: string) => {
    try {
      await disable({ code: value }).unwrap();
      setDisableDialogOpen(false);
      toast.success("Two-factor authentication turned off.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Invalid code"));
    }
  };

  if (isLoading || !status) {
    return <Skeleton className="h-48 w-full max-w-2xl" />;
  }

  const usingTotp = status.method === "TOTP";

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Two-factor authentication</CardTitle>
        <CardDescription>
          Protect your account with a verification code at sign in. SMS codes are billed at $0.01
          per message; authenticator app codes are free and recommended.
          {status.mandatory && " Your plan requires two-factor authentication."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-3">
            {status.enabled ? (
              <ShieldCheck className="h-5 w-5 text-primary" />
            ) : (
              <ShieldOff className="h-5 w-5 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium">
                {status.enabled
                  ? usingTotp
                    ? "Authenticator app"
                    : "SMS verification"
                  : "Not enabled"}
              </p>
              <p className="text-xs text-muted-foreground">
                {status.enabled
                  ? usingTotp
                    ? `Codes from your authenticator app, free of charge. SMS backup: ${status.maskedPhone ?? "none"}.`
                    : `Codes sent to ${status.maskedPhone ?? "your phone"} at $0.01 per message.`
                  : "Add your mobile number to receive verification codes."}
              </p>
            </div>
          </div>
          {status.enabled && <Badge variant="secondary">Enabled</Badge>}
        </div>

        <div className="flex flex-wrap gap-2">
          {!status.enabled && (
            <Button onClick={openSmsDialog}>
              <Smartphone className="mr-2 h-4 w-4" />
              Set up SMS verification
            </Button>
          )}
          {status.enabled && !usingTotp && (
            <>
              <Button onClick={openTotpDialog}>
                Switch to an authenticator app (free)
              </Button>
              <Button variant="outline" onClick={openSmsDialog}>
                Change phone number
              </Button>
            </>
          )}
          {status.enabled && usingTotp && (
            <Button variant="outline" onClick={openSmsDialog}>
              Switch back to SMS
            </Button>
          )}
          {status.enabled && !status.mandatory && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title="Options">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-destructive" onClick={openDisableDialog}>
                  <ShieldOff className="mr-2 h-4 w-4" />
                  Turn off two-factor
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>

      <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{smsCodeSent ? "Enter the code" : "Verify your mobile number"}</DialogTitle>
            <DialogDescription>
              {smsCodeSent
                ? "Enter the 6-digit code we sent to your phone."
                : "We'll text a 6-digit code to this number to verify it."}
            </DialogDescription>
          </DialogHeader>
          {!smsCodeSent && (
            <div className="space-y-1.5">
              <Label htmlFor="smsPhone">Mobile number</Label>
              <Input
                id="smsPhone"
                type="tel"
                placeholder="+1 555 123 4567"
                value={smsPhone}
                onChange={(event) => setSmsPhone(event.target.value)}
              />
            </div>
          )}
          {smsCodeSent && (
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={smsCode} onChange={setSmsCode} onComplete={onConfirmSms} autoFocus>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          )}
          <DialogFooter>
            {!smsCodeSent && (
              <Button onClick={onSendSmsSetupCode} disabled={!smsPhone || smsSetupResult.isLoading}>
                {smsSetupResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send code
              </Button>
            )}
            {smsCodeSent && (
              <Button
                onClick={() => onConfirmSms(smsCode)}
                disabled={smsCode.length < 6 || smsConfirmResult.isLoading}
              >
                {smsConfirmResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={totpDialogOpen} onOpenChange={setTotpDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set up your authenticator app</DialogTitle>
            <DialogDescription>
              Scan the QR code with Google Authenticator, 1Password or a similar app, then enter the
              6-digit code it shows.
            </DialogDescription>
          </DialogHeader>
          {!totpSetup && startTotpResult.isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {totpSetup && (
            <div className="space-y-4">
              {/* theme-exempt: QR code must render on a white background to stay scannable */}
              <div className="flex justify-center rounded-lg bg-white p-4">
                <QRCodeSVG value={totpSetup.otpauthUri} size={180} />
              </div>
              <div className="space-y-1.5">
                <Label>Or enter this key manually</Label>
                <Input readOnly value={totpSetup.secret} onFocus={(event) => event.target.select()} />
              </div>
              <div className="flex justify-center py-2">
                <InputOTP maxLength={6} value={totpCode} onChange={setTotpCode} onComplete={onConfirmTotp}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => onConfirmTotp(totpCode)}
              disabled={!totpSetup || totpCode.length < 6 || confirmTotpResult.isLoading}
            >
              {confirmTotpResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Turn off two-factor authentication</DialogTitle>
            <DialogDescription>
              {usingTotp
                ? "Enter a code from your authenticator app to confirm."
                : "We'll text you a code to confirm it's you."}
            </DialogDescription>
          </DialogHeader>
          {!usingTotp && !disableCodeSent && (
            <Button onClick={onSendDisableCode} disabled={sendCodeResult.isLoading}>
              {sendCodeResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send code
            </Button>
          )}
          {(usingTotp || disableCodeSent) && (
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={disableCode} onChange={setDisableCode} onComplete={onDisable} autoFocus>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => onDisable(disableCode)}
              disabled={disableCode.length < 6 || disableResult.isLoading}
            >
              {disableResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Turn off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
